import { createContext, useCallback, useContext, useMemo, useRef } from 'react';

import type { AngleMode } from '@/components/task/formula-eval';

/**
 * WHICH ANGLE MODE EACH FORMULA FIELD USES, supplied once per block.
 *
 * The alternative was two more props on every `<FormulaField>`, and there are 44 of them across
 * twelve files. That is the shape this codebase keeps getting hurt by: the same fact wired by
 * hand in forty places, where forgetting one `?? fallback` is a silent wrong answer rather than
 * a type error. A block provides this once and every field inside it owns its own mode without
 * its call site changing at all.
 *
 * Keyed by `fieldId`, which is already the stable, required key the undo stack addresses fields
 * by — `data-field-id` IS the data key — so there is no second identity to keep in step.
 */
export interface AngleModeScope {
  /** This field's own mode, or undefined when it has never been set. */
  get: (fieldId: string) => AngleMode | undefined;
  set: (fieldId: string, mode: AngleMode) => void;
  /**
   * The block's old block-wide mode, which seeds every field that has not been set individually.
   *
   * It is read and never written: the per-field control writes `get`/`set`, so a block drains
   * into per-field values as its fields are touched and the old value simply stops being
   * consulted. That is what lets this ship without a migration pass over stored data.
   */
  fallback?: AngleMode;
}

const AngleModeContext = createContext<AngleModeScope | null>(null);

export function AngleModeProvider({ modes, fallback, onChange, children }: {
  /** The block's stored map. Absent is the same as empty. */
  modes: Record<string, AngleMode> | undefined;
  fallback?: AngleMode;
  /** MERGED by the caller, never enumerated: a write that lists only the key it changed deletes
   *  every other field's mode. */
  onChange: (fieldId: string, mode: AngleMode) => void;
  children: React.ReactNode;
}) {
  const scope = useMemo<AngleModeScope>(() => ({
    get: (fieldId) => modes?.[fieldId],
    set: onChange,
    fallback,
  }), [modes, fallback, onChange]);
  return <AngleModeContext.Provider value={scope}>{children}</AngleModeContext.Provider>;
}

/**
 * The mode a field should use, and how to change it.
 *
 * `explicit` is the `angleMode` prop a caller passed by hand. It loses to a stored per-field
 * value and beats the bare default, which is what makes a block's old block-wide setting behave
 * as a seed rather than an override.
 */
export function useAngleMode(fieldId: string | undefined, explicit: AngleMode | undefined) {
  const scope = useContext(AngleModeContext);
  const owned = fieldId !== undefined && scope ? scope.get(fieldId) : undefined;
  const mode: AngleMode = owned ?? explicit ?? scope?.fallback ?? 'degrees';
  const setMode = fieldId !== undefined && scope
    ? (next: AngleMode) => scope.set(fieldId, next)
    : undefined;
  return { mode, setMode };
}

/**
 * The three props a block hands `InputBlockFrame`, built from its data and its own writer.
 *
 * The MERGE lives here and nowhere else. A write that listed only the key it changed would
 * delete every other field's mode — the same deletes-by-omission trap this codebase has hit
 * with tasks and with block banks — so getting it right once is the point of the hook.
 *
 * `write` is the irreducible per-block part: no two block editors store their data the same way,
 * so each supplies "merge this patch into my data" in one line and the rest is shared.
 */
export function useBlockAngleModes(
  data: Record<string, unknown> | undefined,
  write: (patch: { angleModes: Record<string, AngleMode> }) => void,
) {
  const modes = data?.angleModes as Record<string, AngleMode> | undefined;
  const fallback = data?.angleMode as AngleMode | undefined;

  // Through a ref, so a caller passing an inline arrow — which every one of them does — does not
  // rebuild the context value on every render and re-render every formula field under it.
  const latest = useRef({ modes, write });
  latest.current = { modes, write };

  const onAngleModeChange = useCallback((fieldId: string, mode: AngleMode) => {
    const { modes: current, write: send } = latest.current;
    send({ angleModes: { ...(current ?? {}), [fieldId]: mode } });
  }, []);

  return { angleModes: modes, angleModeFallback: fallback, onAngleModeChange };
}
